import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import eOpsLogo from "../images/eops.png";
import { getLogo, companyName } from "../services/Functions";
import { extractDiscoCodeFromApplicationId } from "./adminCompany";

const BRAND_BLUE = [0, 106, 209];
const HEADER_BG = [238, 245, 252];
const TOTAL_ROW_BG = [232, 242, 255];
const BORDER_COLOR = [219, 227, 238];
const FOOTER_BG = [255, 255, 255];
const CURRENT_ROW_BG = [255, 251, 245];
const CURRENT_ROW_BORDER = [253, 186, 116];
const CURRENT_ACCENT = [249, 115, 22];
const CURRENT_TEXT = [194, 65, 12];
const CURRENT_TEXT_DARK = [154, 52, 18];
const CURRENT_BADGE_BG = [255, 237, 213];

export const ACTIVITY_LOG_TIMELINE_TYPES = {
  MCO: "mco",
  MRC: "mrc",
  RECONNECTION: "reconnection",
  NEW_CONNECTION: "newConnection",
  CHANGE_OF_ATTRIBUTES: "changeOfAttributes",
};

const TIMELINE_TYPE_TITLES = {
  [ACTIVITY_LOG_TIMELINE_TYPES.MCO]: "Meter Change Order (MCO)",
  [ACTIVITY_LOG_TIMELINE_TYPES.MRC]: "Meter Reading Correction (MRC)",
  [ACTIVITY_LOG_TIMELINE_TYPES.RECONNECTION]: "Reconnection",
  [ACTIVITY_LOG_TIMELINE_TYPES.NEW_CONNECTION]: "New Connection",
  [ACTIVITY_LOG_TIMELINE_TYPES.CHANGE_OF_ATTRIBUTES]: "Change of Attributes",
};

function getTimelineTitle(timelineType) {
  return TIMELINE_TYPE_TITLES[timelineType] || "Progress Timeline";
}

function statusLabel(status) {
  switch (status) {
    case "delayed":
      return "Delayed";
    case "near-sla":
      return "Near SLA";
    case "pending":
      return "Pending";
    default:
      return "On Time";
  }
}

function getStatusStyles(status, isPending, isCurrentStep = false) {
  if (isCurrentStep) {
    return { fillColor: CURRENT_BADGE_BG, textColor: CURRENT_TEXT };
  }
  if (isPending || status === "pending") {
    return { fillColor: [241, 245, 249], textColor: [100, 116, 139] };
  }
  if (status === "delayed") {
    return { fillColor: [254, 242, 242], textColor: [185, 28, 28] };
  }
  if (status === "near-sla") {
    return { fillColor: [255, 251, 235], textColor: [180, 83, 9] };
  }
  return { fillColor: [236, 253, 243], textColor: [21, 128, 61] };
}

function formatDateTimeCell(row) {
  if (!row.dateTime || row.dateTime === "-") return "-";
  return row.dateTimeDay
    ? `${row.dateTime}\n${row.dateTimeDay}`
    : row.dateTime;
}

function getProgressFillColor(status, isPending) {
  if (isPending || status === "pending") return [203, 213, 225];
  if (status === "delayed") return [239, 68, 68];
  if (status === "near-sla") return [245, 158, 11];
  return [34, 197, 94];
}

function getProgressTextColor(status, isPending) {
  if (isPending || status === "pending") return [148, 163, 184];
  if (status === "delayed") return [185, 28, 28];
  if (status === "near-sla") return [180, 83, 9];
  return [21, 128, 61];
}

function drawProgressBarCell(doc, cell, row) {
  if (!row?.hasProgress || row.isDocumentSubmission) return;

  const padding = 2;
  const trackX = cell.x + padding;
  const trackY = cell.y + 2.2;
  const trackW = cell.width - padding * 2;
  const trackH = 2.4;
  const fillW = Math.max(
    (Math.min(row.progressBarWidth || 0, 100) / 100) * trackW,
    row.progressBarWidth > 0 ? 0.9 : 0,
  );

  doc.setFillColor(232, 238, 245);
  doc.roundedRect(trackX, trackY, trackW, trackH, 1.2, 1.2, "F");

  if (fillW > 0) {
    doc.setFillColor(...getProgressFillColor(row.status, row.isPending));
    doc.roundedRect(trackX, trackY, fillW, trackH, 1.2, 1.2, "F");
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.8);
  doc.setTextColor(...getProgressTextColor(row.status, row.isPending));
  doc.text(
    row.progressLabel || "-",
    cell.x + cell.width / 2,
    trackY + trackH + 3.6,
    { align: "center" },
  );
}

const FUNCTIONS_OUTLINED_PATH =
  "M18 4H6v2l6.5 6L6 18v2h12v-3h-7l5-5-5-5h7z";

let sumIconDataUrlPromise = null;

function getSumIconDataUrl() {
  if (!sumIconDataUrlPromise) {
    sumIconDataUrlPromise = new Promise((resolve, reject) => {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 26 26">
        <circle cx="13" cy="13" r="12" fill="#006ad1" stroke="#ffffff" stroke-width="2"/>
        <g transform="translate(5.5, 5.5) scale(0.625)">
          <path d="${FUNCTIONS_OUTLINED_PATH}" fill="#ffffff"/>
        </g>
      </svg>`;

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 52;
        canvas.height = 52;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Unable to render sum icon"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Unable to load sum icon"));
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    });
  }

  return sumIconDataUrlPromise;
}

function drawTotalSumIcon(doc, cell, iconDataUrl) {
  const size = 6.8;
  const x = cell.x + (cell.width - size) / 2;
  const y = cell.y + (cell.height - size) / 2;
  doc.addImage(iconDataUrl, "PNG", x, y, size, size);
}

function formatProgressCell(row) {
  if (row.isDocumentSubmission) return "-";
  if (!row.hasProgress) return "-";
  return row.progressLabel || "-";
}

function formatStatusCell(row) {
  if (row.isDocumentSubmission) return "-";
  if (row.isCurrentStep && !row.isObjectedCurrentStep) return "In Progress";
  const label = statusLabel(row.isPending ? "pending" : row.status);
  return row.overdueLabel ? `${label}\n${row.overdueLabel}` : label;
}

function drawCurrentStepAccentBar(doc, cell) {
  doc.setFillColor(...CURRENT_ACCENT);
  doc.rect(cell.x, cell.y, 1.4, cell.height, "F");
}

function drawCurrentStepNumber(doc, cell, stepNo) {
  const radius = 2.8;
  const centerX = cell.x + cell.width / 2;
  const centerY = cell.y + cell.height / 2;

  doc.setFillColor(...CURRENT_ACCENT);
  doc.circle(centerX, centerY, radius, "F");
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.35);
  doc.circle(centerX, centerY, radius + 0.55, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.4);
  doc.setTextColor(255, 255, 255);
  doc.text(String(stepNo), centerX, centerY + 0.85, { align: "center" });
}

function drawCurrentStepBadge(doc, cell) {
  const badgeText = "CURRENT STEP";
  const paddingX = 2;
  const badgeHeight = 3.4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.6);
  const badgeWidth = doc.getTextWidth(badgeText) + 4;
  const badgeX = cell.x + paddingX;
  const badgeY = cell.y + (cell.height - badgeHeight) / 2;

  doc.setFillColor(...CURRENT_BADGE_BG);
  doc.setDrawColor(...CURRENT_ROW_BORDER);
  doc.setLineWidth(0.2);
  doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 1.2, 1.2, "FD");

  doc.setFillColor(...CURRENT_ACCENT);
  doc.circle(badgeX + 1.5, badgeY + badgeHeight / 2, 0.55, "F");

  doc.setTextColor(...CURRENT_TEXT);
  doc.text(badgeText, badgeX + 3.2, badgeY + 2.45);
}

function applyCurrentStepCellStyles(data) {
  data.cell.styles.fillColor = CURRENT_ROW_BG;
  data.cell.styles.lineColor = CURRENT_ROW_BORDER;
  data.cell.styles.lineWidth = 0.25;
}

function mapRowToPdfCells(row, index, dataRowsLength) {
  const stepNo = row.isTotalRow
    ? ""
    : String(row.displayStepNo ?? row.stepNo ?? dataRowsLength - index);

  return [
    row.isCurrentStep ? "" : stepNo,
    row.stepName || "-",
    row.isCurrentStep && !row.isObjectedCurrentStep ? "" : row.processedBy || "-",
    formatDateTimeCell(row),
    row.durationLabel || "-",
    row.slaLabel || "-",
    formatProgressCell(row),
    formatStatusCell(row),
    row.remarks || "-",
  ];
}

function getLogoDimensions(companyCode) {
  const compact =
    companyCode === "12" ||
    companyCode === "13" ||
    companyCode === "11" ||
    companyCode === "16" ||
    companyCode === "27" ||
    companyCode === "48" ||
    companyCode === "59";

  return compact
    ? { logoWidth: 25, logoHeight: 20, eOpsX: 43, eOpsY: 12 }
    : { logoWidth: 30, logoHeight: 10, eOpsX: 17, eOpsY: 17 };
}

function resolveCompanyCode(userMeta = {}, applicationDetails = {}) {
  const fromMeta = String(userMeta.companyCode || "").trim().slice(0, 2);
  // Admin code 10 is not a DISCO — never use it for logos (defaults to MEPCO in getLogo).
  if (fromMeta && fromMeta !== "10") {
    return fromMeta;
  }

  const fromApplication = extractDiscoCodeFromApplicationId(
    applicationDetails?.applicationId,
  );
  if (fromApplication) return fromApplication;

  const fromDetails = String(
    applicationDetails?.companyCode || applicationDetails?.discoCode || "",
  )
    .trim()
    .slice(0, 2);
  if (fromDetails && fromDetails !== "10") return fromDetails;

  return "15";
}

function drawReportHeader(
  doc,
  pageWidth,
  margin,
  applicationDetails,
  userMeta = {},
  timelineType,
) {
  const companyCode = resolveCompanyCode(userMeta, applicationDetails);
  const userName = userMeta.name || "-";
  const userRole = userMeta.roleName || "-";
  const { logoWidth, logoHeight, eOpsX, eOpsY } = getLogoDimensions(companyCode);

  doc.addImage(getLogo(companyCode), "PNG", margin + 2, 5, logoWidth, logoHeight);
  doc.addImage(eOpsLogo, "PNG", eOpsX, eOpsY, 20, 8);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15, 39, 68);
  doc.text(companyName(companyCode), pageWidth / 2, 12, { align: "center" });

  doc.setFontSize(11);
  doc.setTextColor(...BRAND_BLUE);
  doc.text(getTimelineTitle(timelineType), pageWidth / 2, 18, { align: "center" });

  const applicationId = applicationDetails.applicationId || "-";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 90, 100);
  doc.text(`Progress Timeline of ${applicationId}`, pageWidth / 2, 24, {
    align: "center",
  });

  doc.setFontSize(8);
  doc.text(
    new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
    pageWidth - margin,
    10,
    { align: "right" },
  );
  doc.text(`Name: ${userName}`, pageWidth - margin, 15, { align: "right" });
  doc.text(`Designation: ${userRole}`, pageWidth - margin, 20, {
    align: "right",
  });

  return 30;
}

function drawLegendCircle(doc, x, y, color, filled = false) {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.55);
  if (filled) {
    doc.setFillColor(...color);
    doc.circle(x, y, 2.1, "FD");
  } else {
    doc.circle(x, y, 2.1, "S");
  }
}

function drawInfoIcon(doc, x, y) {
  doc.setFillColor(...BRAND_BLUE);
  doc.circle(x, y, 2.4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(255, 255, 255);
  doc.text("i", x, y + 0.8, { align: "center" });
}

function drawLegendItem(doc, x, y, item) {
  if (item.pending) {
    drawLegendCircle(doc, x, y, item.color, false);
  } else {
    drawLegendCircle(doc, x, y, item.color, false);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  doc.setTextColor(51, 65, 85);
  doc.text(item.label, x + 5, y + 0.6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.6);
  doc.setTextColor(100, 116, 139);
  doc.text(item.sub, x + 5, y + 4.2);
}

function appendLegends(doc, startY, pageWidth, margin) {
  const boxWidth = pageWidth - margin * 2;
  const topPadding = 7;
  const bottomPadding = 2.5;
  const contentHeight = 10.5;
  const boxHeight = topPadding + contentHeight + bottomPadding;
  const y = startY + 4;
  const bottomY = y + boxHeight;
  const contentY = y + topPadding;

  doc.setFillColor(...FOOTER_BG);
  doc.rect(margin, y, boxWidth, boxHeight, "F");

  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.35);
  doc.line(margin, y, margin + boxWidth, y);
  doc.line(margin, bottomY, margin + boxWidth, bottomY);

  const col1Width = boxWidth * 0.42;
  const col2Width = boxWidth * 0.24;
  const col1X = margin + 5;
  const col2X = margin + col1Width + 4;
  const col3X = margin + col1Width + col2Width + 6;

  const legendItems = [
    { label: "On Time", sub: "0-80% of SLA", color: [34, 197, 94] },
    { label: "Near SLA", sub: "80-100% of SLA", color: [245, 158, 11] },
    { label: "Delayed", sub: ">100% of SLA", color: [239, 68, 68] },
    { label: "Pending", sub: "(Not Processed)", color: [148, 163, 184], pending: true },
  ];

  legendItems.forEach((item, index) => {
    drawLegendItem(doc, col1X + index * 28, contentY, item);
  });

  drawInfoIcon(doc, col2X, contentY - 1);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  doc.setTextColor(51, 65, 85);
  doc.text("SLA:", col2X + 5, contentY);
  doc.setFont("helvetica", "normal");
  doc.text("Service Level Agreement", col2X + 5, contentY + 3.8);
  doc.setFontSize(6.6);
  doc.setTextColor(100, 116, 139);
  const slaLines = doc.splitTextToSize(
    "Time allowed to complete each step",
    col2Width - 8,
  );
  doc.text(slaLines, col2X + 5, contentY + 7.5);

  drawInfoIcon(doc, col3X, contentY - 1);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  doc.setTextColor(51, 65, 85);
  doc.text("Progress Calculation", col3X + 5, contentY);

  doc.setFontSize(6.6);
  doc.setTextColor(...BRAND_BLUE);
  doc.text("Duration", col3X + 5, contentY + 4.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(" = Current Step Date - Previous Step Date", col3X + 15.5, contentY + 4.5);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BRAND_BLUE);
  doc.text("Progress %", col3X + 5, contentY + 8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(" = (Duration / SLA Max) x 100", col3X + 18.5, contentY + 8.5);

  return bottomY + 4;
}

function addPageNumbers(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 130, 140);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 18, pageHeight - 6, {
      align: "right",
    });
  }
}

export async function downloadActivityLogDetailPdf({
  applicationDetails = {},
  applicationDetailFields = [],
  rows = [],
  totalRow = null,
  fileName,
  userMeta = {},
  timelineType,
} = {}) {
  const sumIconDataUrl = await getSumIconDataUrl();
  const doc = new jsPDF("landscape", "mm", "a4", { compressPdf: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  let startY = drawReportHeader(
    doc,
    pageWidth,
    margin,
    applicationDetails,
    userMeta,
    timelineType,
  );

  const detailLabels = applicationDetailFields.map(({ label }) => label);
  const detailValues = applicationDetailFields.map(({ key }) =>
    applicationDetails[key] != null && String(applicationDetails[key]).trim()
      ? String(applicationDetails[key])
      : "-",
  );

  autoTable(doc, {
    startY,
    head: [detailLabels],
    body: [detailValues],
    theme: "grid",
    styles: {
      fontSize: 8.5,
      cellPadding: 3,
      overflow: "linebreak",
      valign: "middle",
      halign: "left",
      lineColor: BORDER_COLOR,
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: [92, 107, 125],
      fontStyle: "bold",
      halign: "left",
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      textColor: [43, 58, 74],
      fontStyle: "bold",
    },
    margin: { left: margin, right: margin },
    tableWidth: pageWidth - margin * 2,
  });

  const tableHeaders = [
    "#",
    "Step Name",
    "Processed By",
    "Date & Time",
    "Duration",
    "SLA (Max)",
    "Progress",
    "Status",
    "Remarks",
  ];

  const bodyRows = rows.map((row, index) =>
    mapRowToPdfCells(row, index, rows.length),
  );

  if (totalRow) {
    bodyRows.push(mapRowToPdfCells(totalRow, rows.length, rows.length));
  }

  if (bodyRows.length === 0) {
    bodyRows.push([
      {
        content: "No timeline data available",
        colSpan: tableHeaders.length,
        styles: {
          halign: "center",
          fontStyle: "bold",
          textColor: [185, 28, 28],
          fillColor: [254, 226, 226],
        },
      },
    ]);
  }

  const totalRowIndex = totalRow ? bodyRows.length - 1 : -1;

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 5,
    head: [tableHeaders],
    body: bodyRows,
    theme: "striped",
    styles: {
      fontSize: 7.5,
      halign: "left",
      valign: "middle",
      cellPadding: 2.2,
      overflow: "linebreak",
      lineColor: BORDER_COLOR,
      lineWidth: 0.2,
      textColor: [43, 58, 74],
    },
    headStyles: {
      fillColor: BRAND_BLUE,
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      0: { cellWidth: 14, halign: "center", fontStyle: "bold", overflow: "visible" },
      1: { cellWidth: 42, fontStyle: "bold", overflow: "linebreak" },
      2: { cellWidth: 36, overflow: "linebreak" },
      3: { cellWidth: 42, overflow: "linebreak" },
      4: { cellWidth: 22, halign: "center", fontStyle: "bold" },
      5: { cellWidth: 19, halign: "center", overflow: "linebreak" },
      6: { cellWidth: 30, halign: "center", minCellHeight: 11, overflow: "visible" },
      7: { cellWidth: 24, halign: "center", overflow: "linebreak" },
      8: { cellWidth: 48, overflow: "linebreak", valign: "top" },
    },
    margin: { left: margin, right: margin },
    didParseCell(data) {
      if (data.section !== "body") return;

      const row =
        data.row.index === totalRowIndex ? totalRow : rows[data.row.index];

      if (totalRowIndex >= 0 && data.row.index === totalRowIndex) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = TOTAL_ROW_BG;
        data.cell.styles.textColor = BRAND_BLUE;
        data.cell.styles.overflow = "visible";

        if (data.column.index === 0) {
          data.cell.text = [""];
          data.cell.styles.halign = "center";
          data.cell.styles.valign = "middle";
        }

        if (
          data.column.index === 6 &&
          totalRow?.hasProgress &&
          !totalRow.isDocumentSubmission
        ) {
          data.cell.text = [""];
          data.cell.styles.minCellHeight = 11;
          data.cell.styles.cellPadding = {
            top: 1.5,
            bottom: 1.5,
            left: 1.5,
            right: 1.5,
          };
        }
        return;
      }

      if (row?.isCurrentStep) {
        applyCurrentStepCellStyles(data);
        data.cell.styles.minCellHeight = 14;

        if (data.column.index === 0) {
          data.cell.text = [""];
          data.cell.styles.halign = "center";
          data.cell.styles.valign = "middle";
          data.cell.styles.overflow = "visible";
        }

        if (data.column.index === 1) {
          data.cell.styles.textColor = CURRENT_TEXT_DARK;
          data.cell.styles.fontStyle = "bold";
        }

        if (data.column.index === 2 && !row.isObjectedCurrentStep) {
          data.cell.text = [""];
          data.cell.styles.overflow = "visible";
          data.cell.styles.valign = "middle";
        }

        if (data.column.index === 4) {
          data.cell.styles.textColor = CURRENT_TEXT;
          data.cell.styles.fontStyle = "bold";
        }

        if (data.column.index === 7 && !row.isDocumentSubmission) {
          const { fillColor, textColor } = getStatusStyles(
            row.status,
            row.isPending,
            row.isCurrentStep && !row.isObjectedCurrentStep,
          );
          data.cell.styles.fillColor = fillColor;
          data.cell.styles.textColor = textColor;
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }

        if (data.column.index === 6 && row.hasProgress && !row.isDocumentSubmission) {
          data.cell.text = [""];
          data.cell.styles.minCellHeight = 11;
          data.cell.styles.cellPadding = {
            top: 1.5,
            bottom: 1.5,
            left: 1.5,
            right: 1.5,
          };
        }
        return;
      }

      if (data.column.index === 7) {
        if (row && !row.isDocumentSubmission) {
          const { fillColor, textColor } = getStatusStyles(
            row.status,
            row.isPending,
          );
          data.cell.styles.fillColor = fillColor;
          data.cell.styles.textColor = textColor;
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      }

      if (data.column.index === 6) {
        if (row?.hasProgress && !row.isDocumentSubmission) {
          data.cell.text = [""];
          data.cell.styles.minCellHeight = 11;
          data.cell.styles.cellPadding = {
            top: 1.5,
            bottom: 1.5,
            left: 1.5,
            right: 1.5,
          };
        }
      }
    },
    didDrawCell(data) {
      if (data.section !== "body") return;

      const row =
        data.row.index === totalRowIndex ? totalRow : rows[data.row.index];

      if (data.column.index === 0 && totalRowIndex >= 0 && data.row.index === totalRowIndex) {
        drawTotalSumIcon(doc, data.cell, sumIconDataUrl);
        return;
      }

      if (row?.isCurrentStep) {
        if (data.column.index === 0) {
          drawCurrentStepAccentBar(doc, data.cell);
          const stepNo = row.displayStepNo ?? row.stepNo ?? "";
          drawCurrentStepNumber(doc, data.cell, stepNo);
          return;
        }

        if (data.column.index === 1) {
          return;
        }

        if (data.column.index === 2 && !row.isObjectedCurrentStep) {
          drawCurrentStepBadge(doc, data.cell);
          return;
        }

        if (data.column.index === 6 && row.hasProgress && !row.isDocumentSubmission) {
          drawProgressBarCell(doc, data.cell, row);
        }
        return;
      }

      if (data.column.index !== 6) return;

      if (row?.hasProgress && !row.isDocumentSubmission) {
        drawProgressBarCell(doc, data.cell, row);
      }
    },
  });

  let legendY = doc.lastAutoTable.finalY + 4;
  if (legendY > pageHeight - 34) {
    doc.addPage();
    legendY = 16;
  }
  appendLegends(doc, legendY, pageWidth, margin);
  addPageNumbers(doc);

  const applicationId =
    applicationDetails.applicationId ||
    applicationDetails.referenceNo ||
    "Progress_Timeline";
  const resolvedFileName =
    fileName || `Progress_Timeline_${applicationId}.pdf`;

  doc.save(resolvedFileName.replace(/[<>:"/\\|?*]+/g, "_"));
}
